import React, { useContext, useMemo, useState } from 'react'

import { InputGroup } from '../../components/InputGroup'
import { FormLabel } from '../../components/FormLabel'
import { Box, FormControl, Text } from '@chakra-ui/react'
import { Discount, InsurerDiscount } from 'BrokerTsLib/src/dtos'
import { Controller, useFormContext } from 'react-hook-form'
import { DiscountBase } from 'BrokerTsLib/src/dtos/StepBase'
import { insuranceParamsContext } from '../../layouts/Wizard'

const DiscountInput = ({ discount, isDisabled = false }: { discount: Discount, isDisabled?: boolean }) => {
  const { minDiscount, maxDiscount, name, discount: value } = discount

  const { getValues, control, getFieldState } = useFormContext<DiscountBase>()
  const allDiscounts = getValues('discounts') ?? []
  var discountIndex = allDiscounts.findIndex(x => x.insuranceCategoryDiscountId === discount.insuranceCategoryDiscountId)

  return (
    <FormControl isInvalid={!!getFieldState(`discounts.${discountIndex}.discount`).error}>
      <FormLabel className="business-discount-label" sx={{ fontSize: 'sm', fontWeight: 'normal', whiteSpace: 'nowrap' }}>{name}</FormLabel>

      <Controller
        name={`discounts.${discountIndex}.discount`}
        defaultValue={value || maxDiscount}
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <InputGroup
            type="number"
            value={value ?? ''}
            htmlSize={1}
            maxLength={2}
            isDisabled={isDisabled}
            min={minDiscount}
            max={maxDiscount}
            pattern="[0-9]{0,2}"
            selectAllonFocus
            sx={{ w: 12 }}
            onChange={(e) => onChange(parseInt(e.currentTarget.value))}
          />
        )}
      />
    </FormControl>
  )
}

const DiscountLimits = ({ discounts, limit }: { discounts: Discount[]; limit: 'minDiscount' | 'maxDiscount' }) => {
  const keys = useMemo(() => Object.keys(discounts), [discounts])

  return (
    <>
      {discounts.map((discount, i) => {
        return (
          <span key={discount.insuranceCategoryDiscountId}>
            {`${discount.name} ${discount[limit]}`}
            {i < keys.length - 1 && ', '}
          </span>
        )
      })}
    </>
  )
}

export const BusinessCompanyDiscount = ({ insurerName, discounts }: InsurerDiscount) => {
  const { insuranceParams } = useContext(insuranceParamsContext)!
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const minimalDiscounts = useMemo(
    () => <DiscountLimits discounts={discounts} limit="minDiscount" />,
    [discounts],
  )
  const maximalDiscounts = useMemo(
    () => <DiscountLimits discounts={discounts} limit="maxDiscount" />,
    [discounts],
  )

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 4,
        gridTemplateColumns: '60% auto',
        justifyContent: 'start',
        justifyItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: 1 }} as="header">
        <p className="business-discounts-company__name">{insurerName}</p>

        <Text
          variant="textMuted"
          sx={{ fontSize: 'xs' }}
          as="p"
          className="business-discounts-company__limit"
        >
          Maximální slevy: {maximalDiscounts}
          <br />
          Minimální slevy: {minimalDiscounts}
        </Text>
      </Box>

      {discounts.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {discounts.map((discount) => {
            return <DiscountInput key={discount.insuranceCategoryDiscountId} discount={discount} isDisabled={isContracted} />
          })}
        </Box>
      )}
    </Box>
  )
}
